import { ChangeDetectionStrategy, Component, inject, ViewChild } from '@angular/core';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { IVoucherType } from '../../core/interfaces/IVoucherType';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { NumberToCurrencyPipe } from '../../../../shared/pipes/number-to-currency.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { CartService } from '../../../cart/core/services/CartService';
import { AddItemsToCart } from '../../../../core/types';
import { isFixedAmountTrackOnlyExternal } from '../../core/utils/fixedAmountTrackOnlyExternal';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-voucher-amount-picker-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    ContentBoxComponent,
    ContentEditableInputComponent,
    KeyboardComponent,
    NumberToCurrencyPipe
  ],
  templateUrl: './voucher-amount-picker-modal.component.html',
  styleUrl: './voucher-amount-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VoucherAmountPickerModalComponent extends BaseModalComponent<IVoucherType> {

  @ViewChild(ContentEditableInputComponent) public contentEditableInputComponent: ContentEditableInputComponent;

  private readonly _cartService = inject(CartService);

  public amountValue: string;

  private _isAmountValueSet = true;

  public override open(val?: IVoucherType): void {
    super.open(val);
    this.amountValue = null;
    this._isAmountValueSet = true;
    this.updateAmountValue(this.min.toString());
  }

  public get isConfirmDisabled(): boolean {
    return Number(this.amountValue) > this.max || Number(this.amountValue) < this.min;
  }

  public handleKeyboardClear(): void {
    this.updateAmountValue('');
  }

  public handleKeyboardDeleteBack(): void {
    this.updateAmountValue(this.amountValue.substring(0, this.amountValue.length - 1));
  }

  public handleKeyboardKeyClick(val: string): void {
    if (this._isAmountValueSet) {
      this._isAmountValueSet = false;
      this.handleKeyboardClear();
    }

    this.updateAmountValue(this.amountValue ? `${this.amountValue}${val}` : val);
  }

  public addVoucherToCart(btn: HTMLButtonElement): void {
    const isTrackOnly = isFixedAmountTrackOnlyExternal(this.data);
    const itemType = isTrackOnly
      ? CartItemTypes.ExternalVoucher
      : (this.data?.keyExternalApi != null || this.data?.flagDistributor)
        ? CartItemTypes.CashVoucher
        : CartItemTypes.Voucher;

    const items: AddItemsToCart = [
      {
        itemId: this.data.voucherTypeId,
        itemType,
        quantity: 1,
        price: this.contentEditableInputComponent.value.fromCurrency(),
        ...(isTrackOnly ? { voucherNumber: this.generateSessionVoucherNumber() } : {})
      }
    ];

    btn.classList.add('disabled');

    this._cartService.addItemToCart(items)
      .pipe(
        finalize(() => btn.classList.remove('disabled')),
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-voucher-item-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private get max(): number {
    return this.data?.amountMax * 100;
  }

  private get min(): number {
    return this.data?.amountMin * 100;
  }

  private updateAmountValue(val: string): void {
    this.amountValue = val;

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = this.amountValue;
    }

    this.cdr.detectChanges();
  }

  private generateSessionVoucherNumber(): string {
    const storageKey = 'cinetixx.cash.distributor-voucher-number';
    const currentValue = Number(sessionStorage.getItem(storageKey) ?? '0');
    const nextValue = Number.isFinite(currentValue) && currentValue > 0 ? currentValue + 1 : 1;

    sessionStorage.setItem(storageKey, String(nextValue));

    return `#${String(nextValue)}`;
  }
}
